<template>
  <div class="evaluation-section">
    <div class="evaluation-header">
      <h3 class="section-title">
        评价
        <span v-if="showScore" class="score-info">({{ averageScore }}分/{{ totalCount }}条)</span>
      </h3>
      <el-button type="primary" size="small" @click="openEvaluationDialog" class="write-evaluation-btn">
        <div class="btn-content">
          <img class="icon-edit" src="/img/xie.png" alt="" />
          写评价
        </div>
      </el-button>
    </div>

    <!-- 评价列表 -->
    <div class="evaluation-list" v-if="evaluationList.length > 0">
      <div v-for="evaluation in evaluationList" :key="evaluation.id" class="evaluation-item">
        <div class="evaluation-header">
          <div class="user-info">
            <span class="username">{{ maskUsername(evaluation.userName) }}</span>
            <custom-rate :value="evaluation.score" :disabled="true" :max="5" :show-score="false"></custom-rate>
          </div>
          <div class="evaluation-meta">
            <span class="time">{{ formatTime(evaluation.createTime) }}</span>
            <el-button
              v-if="isOperator"
              type="text"
              size="mini"
              @click="deleteEvaluation(evaluation.id)"
              class="delete-btn"
            >
              <i class="el-icon-close"></i>
            </el-button>
          </div>
        </div>
        <div class="evaluation-content">
          {{ evaluation.evaluation }}
        </div>
      </div>
    </div>

    <!-- 暂无评价 -->
    <div v-else class="no-evaluation">
      <p>暂无评价</p>
    </div>

    <!-- 分页 -->
    <el-pagination
      v-if="totalCount > 10"
      @current-change="handlePageChange"
      :current-page="currentPage"
      :page-size="10"
      :total="totalCount"
      layout="prev, pager, next"
      class="evaluation-pagination"
    ></el-pagination>

    <!-- 写评价弹窗 -->
    <el-dialog
      :visible.sync="showEvaluationDialog"
      :title="dialogTitle"
      width="600px"
      :before-close="handleCloseDialog"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      custom-class="custom-dialog"
    >
      <div v-if="unratedAppointments.length > 0">
        <el-form :model="evaluationForm" :rules="formRules" ref="evaluationForm" label-width="120px">
          <el-form-item label="预约单号" prop="appointmentId">
            <el-select v-model="evaluationForm.appointmentId" placeholder="请选择预约单号">
              <el-option
                v-for="appointment in unratedAppointments"
                :key="appointment"
                :label="appointment"
                :value="appointment"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="满意度" prop="score">
            <div class="rating-stars">
              <custom-rate
                v-model="evaluationForm.score"
                :max="5"
                :disabled="false"
                :show-score="false"
                @change="handleScoreChange"
              ></custom-rate>
              <span class="satisfaction-text">{{ getSatisfactionText(evaluationForm.score) }}</span>
            </div>
          </el-form-item>

          <el-form-item label="评价描述" prop="evaluation">
            <el-input
              v-model="evaluationForm.evaluation"
              type="textarea"
              :rows="4"
              placeholder="请输入评价描述"
              maxlength="200"
              show-word-limit
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      <div v-else class="no-appointments">
        <p>没有可评价的预约单</p>
      </div>

      <div slot="footer" class="dialog-footer" v-if="unratedAppointments.length > 0">
        <el-button type="primary" @click="handleSubmitEvaluation" :loading="saving" class="submit-btn">提交</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {
  getEvaluationRecords,
  submitEvaluation,
  deleteEvaluation as deleteEvaluationApi,
  getUnratedAppointments
} from '@/api/consultingService'
import CustomRate from '@/components/custom-rate.vue'

export default {
  name: 'EvaluationSection',
  components: {
    CustomRate
  },
  props: {
    consultantId: {
      type: [String, Number],
      required: true
    },
    isOperator: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      evaluationList: [],
      totalCount: 0,
      currentPage: 1,
      showEvaluationDialog: false,
      saving: false,
      unratedAppointments: [],
      evaluationForm: {
        appointmentId: null,
        score: 0,
        evaluation: ''
      },
      formRules: {
        appointmentId: [{required: true, message: '请选择预约单号', trigger: 'change'}],
        score: [
          {required: true, message: '请选择满意度', trigger: 'change'},
          {type: 'number', min: 1, max: 5, message: '满意度必须在1-5之间', trigger: 'change'}
        ],
        evaluation: [
          {required: true, message: '请输入评价描述', trigger: 'blur'},
          {min: 1, max: 200, message: '评价描述长度在1到200个字符', trigger: 'blur'}
        ]
      }
    }
  },
  computed: {
    ...mapState(['UserInfo']),
    // 弹层标题（带图标）
    dialogTitle() {
      return '评价'
    },
    // 是否显示评分（大于等于3条评价时显示）
    showScore() {
      return this.totalCount >= 3
    },

    // 平均分（最多1位小数）
    averageScore() {
      if (this.evaluationList.length === 0) return '0.0'

      const totalScore = this.evaluationList.reduce((sum, item) => sum + item.score, 0)
      const average = totalScore / this.evaluationList.length
      return average.toFixed(1)
    }
  },
  created() {
    this.loadEvaluationRecords()
  },
  methods: {
    // 加载评价记录
    async loadEvaluationRecords() {
      try {
        const response = await getEvaluationRecords({
          consultantId: this.consultantId,
          pageNum: this.currentPage,
          size: 2
        })

        if (response.code === 200) {
          this.evaluationList = response.data.records || []
          this.totalCount = response.data.total || 0
          this.$emit('evaluation-updated', {
            score: this.averageScore,
            count: this.totalCount,
            showScore: this.showScore
          })
        }
      } catch (error) {
        console.error('加载评价记录失败:', error)
        this.$message.error('加载评价记录失败')
      }
    },

    // 处理分页变化
    handlePageChange(page) {
      this.currentPage = page
      this.loadEvaluationRecords()
    },

    // 打开评价弹窗
    async openEvaluationDialog() {
      try {
        const response = await getUnratedAppointments({
          consultantId: this.consultantId,
          pageNum: 1
        })
        if (response.code === 200) {
          this.unratedAppointments = response.data || []

          if (this.unratedAppointments.length === 0) {
            this.$message.warning('没有可评价的预约单')
            return
          }
          this.showEvaluationDialog = true
        }
      } catch (error) {
        // console.error('获取未评价预约单失败:', error)
        // this.$message.error('获取未评价预约单失败')
      }
    },

    // 关闭弹窗
    async handleCloseDialog(forceClose = false) {
      if (forceClose === true) {
        this.showEvaluationDialog = false
        return
      }
      // 检查表单是否有内容
      const hasContent =
        this.evaluationForm.appointmentId || this.evaluationForm.score > 0 || this.evaluationForm.evaluation.trim()

      // if (hasContent) {
      try {
        await this.$confirm('确定要关闭吗？未保存的评价内容将丢失', '确认关闭', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (error) {
        // 用户取消关闭
        if (error !== 'cancel') {
          console.error('关闭弹窗失败:', error)
        }
        return
      }
      // }

      this.showEvaluationDialog = false
      this.$refs.evaluationForm?.resetFields()
      this.evaluationForm = {
        appointmentId: null,
        score: 0,
        evaluation: ''
      }
    },

    // 处理评分变化
    handleScoreChange(score) {
      this.evaluationForm.score = score
    },

    // 获取满意度文案
    getSatisfactionText(score) {
      const satisfactionMap = {
        0: '',
        1: '非常不满意',
        2: '不满意',
        3: '一般般',
        4: '满意',
        5: '非常满意'
      }
      return satisfactionMap[score] || ''
    },

    // 提交评价
    async handleSubmitEvaluation() {
      try {
        await this.$refs.evaluationForm.validate()

        // 提交前确认提示
        try {
          await this.$confirm('确定要提交评价吗？', '确认提交', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'info'
          })
        } catch (error) {
          // 用户取消提交
          if (error !== 'cancel') {
            console.error('确认提交失败:', error)
          }
          return
        }

        // 再次检查预约单是否已被评价
        // const selectedAppointment = this.unratedAppointments.find(item => item.id === this.evaluationForm.appointmentId)
        // if (!selectedAppointment) {
        //   this.$message.error('预约单不存在或已被评价')
        //   return
        // }

        this.saving = true
        const response = await submitEvaluation({
          appointmentId: this.evaluationForm.appointmentId,
          score: this.evaluationForm.score,
          evaluation: this.evaluationForm.evaluation
        })

        if (response.code === 200) {
          this.$message.success('评价提交成功')
          this.handleCloseDialog(true)
          this.loadEvaluationRecords()
        } else {
          this.$message.error(response.msg || '评价提交失败')
        }
      } catch (error) {
        // 表单验证失败或其他错误
        if (error !== 'cancel') {
          console.error('提交评价失败:', error)
          // 表单验证失败时不需要显示错误提示，Element UI 会自动处理
          if (error.message && !error.message.includes('validation')) {
            this.$message.error('评价提交失败')
          }
        }
      } finally {
        this.saving = false
      }
    },

    // 删除评价
    async deleteEvaluation(evaluationId) {
      try {
        await this.$confirm('确定要删除这条评价吗？', '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await deleteEvaluationApi({
          id: evaluationId
        })

        if (response.code === 200) {
          this.$message.success('删除成功')
          this.loadEvaluationRecords()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除评价失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    // 掩码用户名
    maskUsername(username) {
      if (!username) return ''
      if (username.length <= 2) return username
      return username.substring(0, 3) + '****' + username.substring(username.length - 4)
    },

    // 格式化时间（不显示秒）
    formatTime(time) {
      if (!time) return ''
      return new Date(time)
        .toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
        .replace(/\//g, '-')
    }
  }
}
</script>

<style lang="scss" scoped>
.evaluation-section {
  width: 1200px;
  padding: 32px;
  box-sizing: border-box;
  margin-top: 32px;
  background: #fff;
  border-radius: 8px;

  .evaluation-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .section-title {
      font-size: 20px;
      font-weight: 600;
      color: #333;
      margin: 0;
      font-weight: 600;
      .score-info {
        font-size: 14px;
        font-weight: 600;
        color: #999999;
        margin-left: 16px;
      }
    }

    .write-evaluation-btn {
      background: #ff724c;
      border-color: #ff724c;
      border-radius: 8px;
      color: #fff;
      font-size: 18px;
      width: 152px;
      height: 56px;
      .btn-content {
        display: flex;
        align-items: center;
        font-weight: 400;
      }
      .icon-edit {
        width: 30px;
        height: 30px;
        margin-right: 8px;
        margin-left: 15px;
      }
    }
  }

  .evaluation-list {
    .evaluation-item {
      padding: 32px 0;

      &:last-child {
        border-bottom: none;
      }

      .evaluation-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          /deep/ .star-item {
            img {
              width: 24px;
              height: 24px;
            }
          }

          .username {
            font-size: 19px;
            color: #999;
          }

          .stars {
            display: flex;
            align-items: center;
          }
        }

        .evaluation-meta {
          display: flex;
          align-items: center;
          gap: 8px;

          .time {
            font-size: 18px;
            color: #999;
          }

          .delete-btn {
            color: #ff4d4f;
            padding: 4px;
            font-size: 20px;

            &:hover {
              background: #fff2f0;
            }
          }
        }
      }

      .evaluation-content {
        font-size: 18px;
        color: #333;
        line-height: 1.6;
        margin: 0;
      }
    }
  }

  .no-evaluation {
    text-align: center;
    padding: 40px 0;
    color: #999;
  }

  .evaluation-pagination {
    margin-top: 20px;
    text-align: center;
  }
}

::v-deep .custom-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    padding: 24px 40px 20px;
    border-bottom: none;

    .el-dialog__title {
      font-size: 20px;
      font-weight: 600;
      color: #333;
      display: flex;
      align-items: center;
      gap: 8px;

      &::before {
        content: '';
        display: inline-block;
        width: 24px;
        height: 24px;
        background-image: url('/img/pingjia.png');
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }
    }

    .el-dialog__headerbtn {
      top: 24px;
      right: 40px;
    }
  }

  .el-dialog__body {
    padding: 24px 60px;
  }

  .el-dialog__footer {
    padding: 20px 40px 32px;
    border-top: none;
  }

  .el-form {
    .el-form-item {
      margin-bottom: 24px;

      .el-form-item__label {
        font-size: 16px;
        color: #333;
        font-weight: 500;
        padding-bottom: 8px;
        text-align: left;
      }

      .el-form-item__content {
        .el-select,
        .el-input,
        .el-textarea {
          width: 100%;
        }
      }
    }
  }
}

.rating-stars {
  display: flex;
  align-items: center;
  gap: 12px;

  .satisfaction-text {
    font-size: 14px;
    color: #666;
  }
}

.no-appointments {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.dialog-footer {
  text-align: center;
  padding: 0;

  .submit-btn {
    width: 60%;
    height: 48px;
    font-size: 18px;
    font-weight: 500;
    border-radius: 8px;
    background: #5ad3cd;
    border-color: #5ad3cd;

    &:hover {
      background: #4ab8b2;
      border-color: #4ab8b2;
    }
  }
}
</style>
